import styled from '@emotion/styled';
import {
  primary,
  white,
  gray02,
  gray03,
  gray04,
  gray06,
  blueGrayPale,
} from 'presentations/views/styles/color';
import {
  spacing,
  fontSize,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
interface Props {
  content: string | null;
  className?: string;
}

export default function Component(props: Props) {
  if (!props.content) return null;
  return (
    <Description
      className={props.className}
      dangerouslySetInnerHTML={{
        __html: props.content,
      }}
    />
  );
}

const Description = styled.div`
  white-space: pre-wrap;
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial,
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji';
  font-size: ${fontSize(16)};
  line-height: 1.5;
  word-wrap: break-word;

  details,
  figcaption,
  figure {
    display: block;
  }

  summary {
    display: list-item;
  }

  a {
    background-color: transparent;
    color: ${primary};
    text-decoration: none;
  }

  a:active,
  a:hover {
    outline-width: 0;
  }

  b,
  strong {
    font-weight: ${fontWeight('bold')};
  }

  small {
    font-size: ${fontSize(12)};
  }

  sub,
  sup {
    font-size: ${fontSize(12)};
    line-height: 0;
    position: relative;
    vertical-align: baseline;
  }

  sub {
    bottom: -0.25em;
  }

  sup {
    top: -0.5em;
  }

  img {
    border-style: none;
    max-width: 100%;
    box-sizing: content-box;
    background-color: ${white};
  }

  pre {
    font-family: monospace, monospace;
    font-size: ${fontSize(14)};
  }

  figure {
    margin: ${spacing(24)} ${spacing(40)};
  }

  hr {
    box-sizing: content-box;
    overflow: hidden;
    background: transparent;
    border-bottom: 1px solid ${gray03};
    height: 0.25em;
    padding: 0;
    margin: ${spacing(24)} 0;
    background-color: ${gray06};
    border: 0;
  }

  a:hover {
    text-decoration: underline;
  }

  hr::before {
    display: table;
    content: '';
  }

  hr::after {
    display: table;
    clear: both;
    content: '';
  }

  table {
    border-spacing: 0;
    border-collapse: collapse;
    display: block;
    width: max-content;
    max-width: 100%;
    overflow: auto;
  }

  td,
  th {
    padding: 0;
  }

  h1,
  h2,
  h3,
  h4 {
    font-weight: ${fontWeight('bold')};
    line-height: 1.25;
  }

  h1 {
    margin: ${spacing(16)} 0;
    font-size: ${fontSize(20)};
  }

  h2 {
    margin-bottom: ${spacing(8)};
    font-size: ${fontSize(18)};
  }

  h3 {
    margin-bottom: ${spacing(8)};
    font-size: ${fontSize(16)};
  }

  h4 {
    margin-bottom: ${spacing(4)};
    font-size: ${fontSize(14)};
  }

  p {
    margin-top: 0;
    margin-bottom: ${spacing(8)};
  }

  blockquote {
    margin: 0;
    padding: 0 ${spacing(12)};
    color: ${gray02};
    border-left: 0.25em solid ${gray04};
  }

  ul,
  ol {
    margin-top: 0;
    margin-bottom: 0;
    padding-left: ${spacing(24)};
    list-style: inherit;
  }

  ol ol,
  ul ol {
    list-style-type: lower-roman;
  }

  ul ul ol,
  ul ol ol,
  ol ul ol,
  ol ol ol {
    list-style-type: lower-alpha;
  }

  dd {
    margin-left: 0;
  }

  pre {
    margin-top: 0;
    margin-bottom: 0;
    font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas,
      Liberation Mono, monospace;
    font-size: ${fontSize(12)};
    word-wrap: normal;
    white-space: pre-wrap;
  }

  ::before {
    display: table;
    content: '';
  }

  ::after {
    display: table;
    clear: both;
    content: '';
  }

  > *:first-child {
    margin-top: 0 !important;
  }

  > *:last-child {
    margin-bottom: 0 !important;
  }

  a:not([href]) {
    color: inherit;
    text-decoration: none;
  }

  p,
  blockquote,
  ul,
  ol,
  dl,
  table,
  pre,
  details {
    margin-top: 0;
    margin-bottom: ${spacing(8)};
  }

  blockquote > :first-child {
    margin-top: 0;
  }

  blockquote > :last-child {
    margin-bottom: 0;
  }

  sup > a::before {
    content: '[';
  }

  sup > a::after {
    content: ']';
  }

  ul.no-list,
  ol.no-list {
    padding: 0;
    list-style-type: none;
  }

  ol[type='1'] {
    list-style-type: decimal;
  }

  ol[type='a'] {
    list-style-type: lower-alpha;
  }

  ol[type='i'] {
    list-style-type: lower-roman;
  }

  div > ol:not([type]) {
    list-style-type: decimal;
  }

  ul ul,
  ul ol,
  ol ol,
  ol ul {
    margin-top: ${spacing(8)};
    margin-bottom: 0;
  }

  ul {
    list-style-type: none;
    padding-left: 0;
  }
  ul > li {
    position: relative;
    margin-bottom: ${spacing(16)};
    padding-left: 1.8em;

    &:before {
      position: absolute;
      top: 0.25em;
      left: 8px;
      font-family: 'Material Icons';
      font-size: ${fontSize(12)};
      content: 'remove';
      vertical-align: middle;
    }
  }

  li > p {
    margin-top: ${spacing(16)};
  }

  li + li {
    margin-top: ${spacing(8)};
  }

  dl {
    padding: 0;
  }

  dl dt {
    padding: 0;
    margin-top: ${spacing(16)};
    font-size: ${fontSize(14)};
    font-style: italic;
    font-weight: ${fontWeight('bold')};
  }

  dl dd {
    padding: 0 ${spacing(16)};
    margin-bottom: ${spacing(16)};
  }

  table th {
    font-weight: ${fontWeight('bold')};
  }

  table th,
  table td {
    padding: ${spacing(8)} ${spacing(16)};
    border: 1px solid ${gray04};
  }

  table tr {
    background-color: ${white};
    border-top: 1px solid ${gray03};
  }

  table tr:nth-child(2n) {
    background-color: ${white};
  }

  table img {
    background-color: transparent;
  }

  img[align='right'] {
    padding-left: ${spacing(20)};
  }

  img[align='left'] {
    padding-right: ${spacing(20)};
  }

  .emoji {
    max-width: none;
    vertical-align: text-top;
    background-color: transparent;
  }

  .highlight {
    margin-bottom: ${spacing(16)};
  }

  .highlight pre {
    margin-bottom: 0;
    word-break: normal;
  }

  pre {
    padding: ${spacing(16)};
    overflow: auto;
    font-size: ${fontSize(13)};
    line-height: 1.45;
    background-color: ${blueGrayPale};
    border-radius: ${borderRadius(6)};
  }

  .csv-data td,
  .csv-data th {
    padding: ${spacing(4)};
    overflow: hidden;
    font-size: ${fontSize(12)};
    line-height: 1;
    text-align: left;
    white-space: nowrap;
  }

  .csv-data .blob-num {
    padding: ${spacing(8)};
    text-align: right;
    background: ${white};
    border: 0;
  }

  .csv-data tr {
    border-top: 0;
  }

  .csv-data th {
    font-weight: ${fontWeight('bold')};
    background: ${white};
    border-top: 0;
  }

  .footnotes {
    font-size: ${fontSize(12)};
    color: ${gray02};
    border-top: 1px solid ${gray04};
  }

  .footnotes ol {
    padding-left: ${spacing(16)};
  }

  .footnotes li {
    position: relative;
  }

  .footnotes li:target::before {
    position: absolute;
    top: -8px;
    right: -8px;
    bottom: -8px;
    left: -24px;
    pointer-events: none;
    content: '';
    border: 2px solid ${primary};
    border-radius: ${borderRadius(6)};
  }

  .footnotes li:target {
    color: ${gray02};
  }

  .footnotes .data-footnote-backref g-emoji {
    font-family: monospace;
  }

  .task-list-item {
    list-style-type: none;
  }

  .task-list-item label {
    font-weight: ${fontWeight('bold')};
  }

  .task-list-item.enabled label {
    cursor: pointer;
  }

  .task-list-item + .task-list-item {
    margin-top: ${spacing(4)};
  }

  .task-list-item .handle {
    display: none;
  }

  .task-list-item-checkbox {
    margin: 0 0.2em 0.25em -1.6em;
    vertical-align: middle;
  }

  .contains-task-list:dir(rtl) .task-list-item-checkbox {
    margin: 0 -1.6em 0.25em 0.2em;
  }

  ::-webkit-calendar-picker-indicator {
    filter: invert(50%);
  }
`;
